// services/idb.ts — Platik-IA
// Adaptador de IndexedDB (via librería idb). Fuente de verdad local.
// Los módulos acceden a esto solo a través de sus propios adapters.

import { openDB, type IDBPDatabase } from 'idb';
import type { SyncStatus } from './supabase.types';
import { sheetDateToIso } from '../shared/utils/format';

const DB_NAME    = 'platik-ia';
const DB_VERSION = 1;

// ── Tipos del esquema ────────────────────────────────────────────────────

export interface IDBTransaction {
  id:           string;          // UUID generado en client
  fecha:        string;          // ISO 8601
  tipo:         'Gasto' | 'Ingreso';
  monto:        number;
  descripcion:  string;
  categoria:    string;
  subcategoria: string;
  syncStatus:   SyncStatus;
  syncedAt:     string | null;   // ISO 8601
  createdAt:    string;          // ISO 8601
}

export interface IDBCategory {
  name:          string;         // PK
  subcategories: string[];
}

export interface IDBSetting {
  key:   string;                 // PK
  value: unknown;
}

export interface IDBSyncQueueItem {
  id:          string;           // UUID
  operation:   'create' | 'update' | 'delete';
  entityType:  'transaction' | 'category';
  entityId:    string;
  payload:     unknown;
  createdAt:   string;
  retries:     number;
}

// ── Apertura de la base ───────────────────────────────────────────────────

let _db: IDBPDatabase | null = null;

export async function getDB(): Promise<IDBPDatabase> {
  if (_db) return _db;

  _db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Transacciones
      if (!db.objectStoreNames.contains('transactions')) {
        const txStore = db.createObjectStore('transactions', { keyPath: 'id' });
        txStore.createIndex('fecha',      'fecha');
        txStore.createIndex('tipo',       'tipo');
        txStore.createIndex('categoria',  'categoria');
        txStore.createIndex('syncStatus', 'syncStatus');
      }

      // Categorías
      if (!db.objectStoreNames.contains('categories')) {
        db.createObjectStore('categories', { keyPath: 'name' });
      }

      // Configuración del usuario
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings', { keyPath: 'key' });
      }

      // Cola de sync pendiente
      if (!db.objectStoreNames.contains('syncQueue')) {
        const sq = db.createObjectStore('syncQueue', { keyPath: 'id' });
        sq.createIndex('createdAt', 'createdAt');
      }
    },
  });

  return _db;
}

// ── Helpers de transacciones ──────────────────────────────────────────────

export async function getAllTransactions(): Promise<IDBTransaction[]> {
  const db = await getDB();
  const all = await db.getAll('transactions') as IDBTransaction[];
  return all.sort((a, b) => b.fecha.localeCompare(a.fecha));
}

export async function putTransaction(tx: IDBTransaction): Promise<void> {
  const db = await getDB();
  await db.put('transactions', tx);
}

export async function deleteTransaction(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('transactions', id);
}

export async function getPendingTransactions(): Promise<IDBTransaction[]> {
  const db = await getDB();
  return db.getAllFromIndex('transactions', 'syncStatus', 'pending') as Promise<IDBTransaction[]>;
}

/** Normaliza fechas legacy ("dd/mm/yyyy [hh:mm:ss]" del v2) a ISO local en `fecha` y
 *  `createdAt`. Idempotente: solo reescribe las filas que cambian. */
export async function normalizeLegacyDates(): Promise<void> {
  const db  = await getDB();
  const all = await db.getAll('transactions') as IDBTransaction[];
  const tx  = db.transaction('transactions', 'readwrite');
  for (const t of all) {
    const fecha     = sheetDateToIso(t.fecha);
    const createdAt = sheetDateToIso(t.createdAt);
    if (fecha !== t.fecha || createdAt !== t.createdAt) {
      await tx.store.put({ ...t, fecha, createdAt });
    }
  }
  await tx.done;
}

// ── Helpers de categorías ─────────────────────────────────────────────────

export async function getAllCategories(): Promise<IDBCategory[]> {
  const db = await getDB();
  return db.getAll('categories') as Promise<IDBCategory[]>;
}

export async function putCategories(cats: IDBCategory[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('categories', 'readwrite');
  await tx.store.clear();
  for (const cat of cats) await tx.store.put(cat);
  await tx.done;
}

// ── Helpers de configuración ───────────────────────────────────────────────

export async function getSetting<T>(key: string): Promise<T | null> {
  const db  = await getDB();
  const row = await db.get('settings', key) as IDBSetting | undefined;
  return (row?.value as T) ?? null;
}

export async function setSetting(key: string, value: unknown): Promise<void> {
  const db = await getDB();
  await db.put('settings', { key, value });
}

// ── Cola de sync ──────────────────────────────────────────────────────────

export async function enqueueSyncItem(item: Omit<IDBSyncQueueItem, 'retries'>): Promise<void> {
  const db = await getDB();
  await db.put('syncQueue', { ...item, retries: 0 });
}

export async function getPendingSyncItems(): Promise<IDBSyncQueueItem[]> {
  const db = await getDB();
  const all = await db.getAll('syncQueue') as IDBSyncQueueItem[];
  return all.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function removeSyncItem(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('syncQueue', id);
}

export async function incrementSyncRetry(id: string): Promise<void> {
  const db  = await getDB();
  const item = await db.get('syncQueue', id) as IDBSyncQueueItem | undefined;
  if (item) await db.put('syncQueue', { ...item, retries: item.retries + 1 });
}

/** Resetea el contador de reintentos de todos los items pendientes.
 *  Se llama al iniciar sesión para dar otra oportunidad a items que se quedaron
 *  atascados (p. ej. por un access_token caducado en la sesión anterior). */
export async function resetSyncRetries(): Promise<void> {
  const db  = await getDB();
  const all = await db.getAll('syncQueue') as IDBSyncQueueItem[];
  const tx  = db.transaction('syncQueue', 'readwrite');
  await Promise.all(
    all.filter(item => item.retries > 0)
       .map(item => tx.store.put({ ...item, retries: 0 }))
  );
  await tx.done;
}
